import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  HandCoins,
  PoundSterling,
  User,
  MessageSquare,
  Save,
  X,
  CheckCircle2,
  Calendar,
  Mail,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils/formatters';
import type { ContactUI } from '@/types/ui/customer-detail';

interface RecordPromiseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  totalAmount: number;
  contacts: ContactUI[];
  onSave: (data: RecordPromiseData) => void;
}

export interface RecordPromiseData {
  confirmationMethod: string;
  promiseDate: string;
  notes: string;
  contactId: string;
  sendConfirmation: boolean;
}

export function RecordPromiseDialog({
  open,
  onOpenChange,
  customerName,
  totalAmount,
  contacts,
  onSave,
}: RecordPromiseDialogProps) {
  const [confirmationMethod, setConfirmationMethod] = useState<string>('');
  const [promiseDate, setPromiseDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [contactId, setContactId] = useState<string>('');
  const [sendConfirmation, setSendConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setConfirmationMethod('');
      setPromiseDate(format(new Date(), 'yyyy-MM-dd'));
      setNotes('');
      setSendConfirmation(false);
      setIsSubmitting(false);

      const primaryContact = contacts.find(x => x.isPrimary);
      setContactId(primaryContact?.id ?? '');
    }
  }, [open, contacts]);

  const handleSave = async () => {
    // Validation
    if (!confirmationMethod) {
      toast.error('Please select a confirmation method');
      return;
    }

    if (!promiseDate) {
      toast.error('Please select a promise date');
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the onSave callback with the form data
      onSave({
        confirmationMethod,
        promiseDate,
        notes,
        contactId,
        sendConfirmation,
      });

      // Close the dialog
      onOpenChange(false);
    } catch (error) {
      console.error('Error recording promise:', error);
      toast.error('Failed to record promise');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandCoins className="h-5 w-5 text-teal-600" />
            Record Promise
          </DialogTitle>
          <DialogDescription>
            Record a promise to pay for the selected transactions
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Customer Name */}
          <div className="space-y-2">
            <Label htmlFor="customer" className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              Customer
            </Label>
            <Input
              id="customer"
              value={customerName}
              disabled
              className="bg-muted"
            />
          </div>

          {/* Amount Promised and Promise Date - Same Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Amount Promised */}
            <div className="space-y-2">
              <Label htmlFor="amount" className="flex items-center gap-2">
                <PoundSterling className="h-4 w-4 text-muted-foreground" />
                Amount Promised
              </Label>
              <Input
                id="amount"
                value={formatCurrency(totalAmount)}
                disabled
                className="bg-muted font-semibold"
              />
            </div>

            {/* Promise Date */}
            <div className="space-y-2">
              <Label htmlFor="promiseDate" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Promise Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="promiseDate"
                type="date"
                value={promiseDate}
                onChange={(e) => setPromiseDate(e.target.value)}
                className="cursor-pointer"
              />
            </div>
          </div>

          {/* Contact */}
          {contacts.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="contact" className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                Contact
              </Label>
              <Select
                value={contactId}
                onValueChange={setContactId}
              >
                <SelectTrigger id="contact">
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map(x => (
                    <SelectItem key={x.id} value={x.id}>
                      {x.firstName} {x.surname}{x.isPrimary ? ' (Primary)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Confirmation Method */}
          <div className="space-y-2">
            <Label htmlFor="confirmationMethod" className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              Confirmation Method <span className="text-destructive">*</span>
            </Label>
            <Select
              value={confirmationMethod}
              onValueChange={setConfirmationMethod}
            >
              <SelectTrigger id="confirmationMethod">
                <SelectValue placeholder="Select confirmation method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Phone">Phone</SelectItem>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="Portal">Portal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              Notes
            </Label>
            <Textarea
              id="notes"
              placeholder="Add any additional notes about this promise..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Send Confirmation */}
          <div className="hidden">
            <Checkbox
              id="sendConfirmation"
              checked={sendConfirmation}
              onCheckedChange={(checked) => setSendConfirmation(checked as boolean)}
            />
            <Label htmlFor="sendConfirmation" className="flex items-center gap-2 cursor-pointer">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Send confirmation email
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSubmitting || !confirmationMethod || !promiseDate}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Saving...' : 'Save Promise'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
